import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { NFT, NFTCustomTag } from '../types';

/**
 * Query key factory for NFT-related queries
 * Centralizes query keys to make invalidation easier
 */
export const nftQueryKeys = {
  nft: (tokenId: string) => ['nft', tokenId] as const,
  offers: (tokenId: string) => ['nft', tokenId, 'offers'] as const,
  history: (tokenId: string) => ['nft', tokenId, 'history'] as const,
};

export interface CurrentOwnerAcquisition {
  acquiredAt: string | null;
  acquiredPrice: number | null;
  acquiredVia: 'mint' | 'purchase' | 'transfer' | null;
  acquiredFrom: string | null;
  currentHoldTimeSeconds: number | null;
}

export interface OwnerMetrics {
  totalProfit: number | null;
  roi: number | null;
  avgHoldTimeSold: number | null;
  avgHoldTimeCurrent: number | null;
}

export interface OfferControls {
  offersDisabled: boolean;
  minOfferPrice: number | null;
  isActive: boolean;
}

export interface NFTValuation {
  estimatedValue: number;
  valueLow: number;
  valueHigh: number;
  confidence: 'high' | 'medium' | 'low';
  confidenceScore: number;
  calculatedAt: string;
}

export interface CollectionTag {
  id: string;
  collection_id: string;
  name: string;
  display_name: string;
  tag_type: 'text' | 'number' | 'url' | 'boolean';
  description: string | null;
  display_order: number;
}

export interface EffectiveOfferControls {
  offersDisabled: boolean;
  effectiveMinimum: number | null;
  source: 'none' | 'nft' | 'wallet' | 'both';
  walletMinimum: number | null;
  nftMinimum: number | null;
  /** True when floor_relative mode is active but no floor price is available */
  noFloorPrice?: boolean;
}

export interface PendingTransferOffer {
  offerId: string;
  fromAddress: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface OutgoingTransferOffer {
  offerId: string;
  toAddress: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface NFTDetailData {
  nft: NFT;
  currentOwnerAcquisition: CurrentOwnerAcquisition;
  ownerMetrics: OwnerMetrics | null;
  ownerHasAccount: boolean;
  ownerHasVerifiedEmail: boolean;
  offerControls: OfferControls;
  effectiveOfferControls: EffectiveOfferControls;
  valuation: NFTValuation | null;
  customTags: NFTCustomTag[];
  canManageCollection: boolean;
  collectionTags: CollectionTag[];
  pendingTransferOffer: PendingTransferOffer | null;
  outgoingTransferOffer: OutgoingTransferOffer | null;
}

/**
 * Hook to fetch NFT details
 * Uses TanStack Query for caching and state management
 */
export function useNFT(tokenId: string | undefined) {
  return useQuery({
    queryKey: nftQueryKeys.nft(tokenId || ''),
    queryFn: async () => {
      if (!tokenId) throw new Error('Token ID is required');

      const response = await apiClient.get<any>(`/nfts/${tokenId}`);

      if (!response.success || !response.data) {
        throw new Error('NFT not found');
      }

      return response.data as NFTDetailData;
    },
    enabled: !!tokenId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

export interface NFTOffersData {
  sellOffers: any[]; // FormattedOffer from API
  buyOffers: any[]; // FormattedOffer from API
}

/**
 * Hook to fetch NFT offers (both sell and buy offers)
 * Uses TanStack Query for caching and state management
 */
export function useNFTOffers(tokenId: string | undefined) {
  return useQuery({
    queryKey: nftQueryKeys.offers(tokenId || ''),
    queryFn: async () => {
      if (!tokenId) throw new Error('Token ID is required');

      const response = await apiClient.get<any>(`/nfts/${tokenId}/offers`);

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch offers');
      }

      const data: NFTOffersData = {
        sellOffers: response.data.sellOffers || [],
        buyOffers: response.data.buyOffers || [],
      };

      return data;
    },
    enabled: !!tokenId,
    staleTime: 15 * 1000, // 15 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

export interface NFTHistoryData {
  transactions: any[]; // Transaction from API
}

/**
 * Hook to fetch NFT transaction history
 * Uses TanStack Query for caching and state management
 */
export function useNFTHistory(tokenId: string | undefined, page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: [...nftQueryKeys.history(tokenId || ''), page, limit] as const,
    queryFn: async () => {
      if (!tokenId) throw new Error('Token ID is required');

      const response = await apiClient.get<any>(`/nfts/${tokenId}/history?page=${page}&limit=${limit}`);

      if (!response.success) {
        throw new Error('Failed to fetch history');
      }

      const data: NFTHistoryData = {
        transactions: response.data.transactions || [],
      };

      return data;
    },
    enabled: !!tokenId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}
